import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { isSidebarPathActive } from './isSidebarPathActive';
import type { SidebarNavLeaf } from './sidebarMenuTypes';
import { flattenSidebarNavPaths } from './sidebarMenuTypes';
import { SidebarNavItem } from './SidebarNavItem';

type SidebarNavBranchProps = {
  item: SidebarNavLeaf;
  siblingPaths: readonly string[];
  collapsed?: boolean;
  onNavigate?: () => void;
};

export function SidebarNavBranch({
  item,
  siblingPaths,
  collapsed = false,
  onNavigate,
}: SidebarNavBranchProps) {
  const location = useLocation();
  const childPaths = flattenSidebarNavPaths(item.children ?? []);
  const allPaths = [...siblingPaths, ...childPaths];
  const isChildActive = childPaths.some((path) =>
    isSidebarPathActive(location.pathname, path, allPaths),
  );
  const isParentActive = isSidebarPathActive(location.pathname, item.to, allPaths);
  const isActiveGroup = isParentActive || isChildActive;
  const [expanded, setExpanded] = useState(isChildActive);

  useEffect(() => {
    if (isChildActive) {
      setExpanded(true);
    }
  }, [isChildActive]);

  const Icon = item.icon;

  function renderChild(child: SidebarNavLeaf) {
    if (child.children && child.children.length > 0) {
      return (
        <SidebarNavBranch
          key={child.code}
          item={child}
          siblingPaths={allPaths}
          collapsed={collapsed}
          onNavigate={onNavigate}
        />
      );
    }

    return (
      <SidebarNavItem
        key={child.code}
        to={child.to}
        label={child.label}
        icon={child.icon}
        collapsed={collapsed}
        onNavigate={onNavigate}
        siblingPaths={allPaths}
      />
    );
  }

  if (collapsed) {
    return (
      <div className="space-y-1">
        <NavLink
          to={item.to}
          title={item.label}
          aria-label={item.label}
          aria-current={isParentActive ? 'page' : undefined}
          onClick={onNavigate}
          className={() =>
            [
              'flex items-center justify-center rounded-xl px-2 py-2.5 text-sm font-medium transition-colors',
              isActiveGroup
                ? 'bg-white/15 text-white'
                : 'text-primary-100 hover:bg-white/10 hover:text-white',
            ].join(' ')
          }
        >
          <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
        </NavLink>
        {expanded ? (item.children ?? []).map((child) => renderChild(child)) : null}
      </div>
    );
  }

  return (
    <div className="space-y-0.5">
      <div className="flex items-center gap-1">
        <NavLink
          to={item.to}
          aria-label={item.label}
          aria-current={isParentActive ? 'page' : undefined}
          onClick={onNavigate}
          className={() =>
            [
              'flex min-w-0 flex-1 items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-colors',
              isParentActive
                ? 'bg-white/15 text-white'
                : 'text-primary-100 hover:bg-white/10 hover:text-white',
            ].join(' ')
          }
        >
          <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
          <span className="truncate">{item.label}</span>
        </NavLink>
        <button
          type="button"
          aria-label={`Expandir ${item.label}`}
          aria-expanded={expanded}
          onClick={() => setExpanded((current) => !current)}
          className={`rounded-lg p-1.5 transition-colors ${
            isActiveGroup
              ? 'text-white hover:bg-white/10'
              : 'text-primary-100 hover:bg-white/10 hover:text-white'
          }`}
        >
          <ChevronDownIcon
            className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
            aria-hidden="true"
          />
        </button>
      </div>

      {expanded ? (
        <div className="ml-3 space-y-0.5 border-l border-white/15 pl-2">
          {(item.children ?? []).map((child) => renderChild(child))}
        </div>
      ) : null}
    </div>
  );
}
